<template>
  <div style="position:relative;">
    <mid-autumn-banner variant="send"/>
    <guide-tour config-key="message.send.line"/>
    <v-stepper v-model="e6" vertical class="elevation-0 mx-auto">
      <v-overlay :value="loading || isPreparingSend" absolute>
        <v-progress-circular indeterminate size="64"/>
      </v-overlay>
      <v-stepper-step :complete="e6 > 1" step="1" editable>
        {{ $t('SendStep.filterTitle') }}
        <small>{{ $t('SendStep.filterDesc') }}</small>
      </v-stepper-step>
      <v-stepper-content step="1">
        <v-card outlined class="mb-12 pa-2">
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="dates"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    :label="$t('SendStep.filterDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="dates" no-title scrollable range locale="zh">
                  <v-spacer/>
                  <v-btn text color="primary" @click="menu = false">
                    取消
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.menu.save(dates)">
                    確認
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox v-model="satisfaction" hide-details :label="$t('SendStep.filterSatisfaction')"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-if="clinicUser" v-model="selectDoctors" multiple chips hide-details clearable
                        :items="doctorItems" item-text="doctor_name" item-value="doctor_code" :label="$t('SendStep.filterDoctor')">
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="toggle">
                    <v-list-item-action>
                      <v-icon :color="selectDoctors.length > 0 ? 'indigo darken-4' : ''">
                        {{ icon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $t('SendStep.selectAll') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>
              <v-select v-else v-model="selectClinic" multiple chips hide-details clearable
                        :items="clinicItems" item-text="clinic_name" item-value="clinic_code" :label="$t('SendStep.filterClinic')">
                <template v-slot:prepend-item>
                  <v-list-item ripple @click="toggle">
                    <v-list-item-action>
                      <v-icon :color="selectDoctors.length > 0 ? 'indigo darken-4' : ''">
                        {{ icon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ $t('SendStep.selectAll') }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="selectedTreatmentTagIds" multiple chips hide-details
                        clearable :items="treatmentTagItems" item-value="id" item-text="name" :label="$t('SendStep.filterTreatmentCategory')"/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="patientName" hide-details :label="$t('SendStep.filterName')" clearable/>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="selectReserveTags"
                hide-details
                label="約診標籤"
                clearable
                multiple
                :items="reserveTags"
                item-value="tag_id"
                item-text="content"
              />
            </v-col>
          </v-row>
        </v-card>
        <v-btn color="primary" @click="filterPatient">{{ $t('SendStep.confirmFilter') }}</v-btn>
        <v-btn text @click="clearFilter">{{ $t('SendStep.clearFilter') }}</v-btn>
      </v-stepper-content>

      <v-stepper-step v-show="!useCondition" :complete="e6 > 2" step="2" :editable="e6 > 1" class="mt-2">
        {{ $t('SendStep.countSelect', { count: selectPatients.length }) }}
      </v-stepper-step>
      <v-stepper-content v-show="!useCondition" step="2">
        <v-card outlined class="mb-12 pa-2">
          <v-row>
            <v-col cols="12" sm="6">
              <v-card>
                <v-card-subtitle class="d-flex">{{ $t('SendStep.patientList') }}
                  <v-btn small color="primary" class="ml-auto" @click="toggleSelectPatient">
                    {{ selectAllPatients ? $t('SendStep.cancelSelect') : $t('SendStep.selectAll') }}
                  </v-btn>
                </v-card-subtitle>
                <v-card-text>
                  <v-text-field v-model.trim="searchPatientName" :label="$t('SendStep.searchName')" outlined dense hide-details
                                style="width: calc(100% - 15px)"/>
                  <v-card flat height="200" class="overflow-y-auto">
                    <v-card v-for="patient in patients" :key="patient" flat link height="40"
                            @click="selectPatient(patient)">
                      <v-checkbox
                        :input-value="selectPatients.includes(patient)"
                        color="blue"
                        :label="patient.split('-')[2]"
                        hide-details dense
                        style="padding-top: 8px"
                        :disabled="selectPatients.length >= maxSelect && !selectPatients.includes(patient)"
                      />
                    </v-card>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6">
              <v-card>
                <v-card-subtitle class="d-flex">{{ $t('SendStep.showSelect') }}
                  <v-btn small color="primary" :disabled="!selectPatients.length" class="ml-auto"
                         @click="selectPatients = []">{{ $t('SendStep.clearAll') }}
                  </v-btn>
                </v-card-subtitle>
                <v-card-text class="overflow-y-auto" style="height: 256px;">
                  <v-chip
                    v-for="(patient, i) in selectPatients"
                    :key="patient"
                    class="ma-2"
                    close
                    color="primary"
                    outlined
                    @click:close="remove(i)"
                  >
                    {{ patient.split('-')[1] }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
        <v-btn color="primary" @click="e6 = 3">{{ $t('SendStep.confirmSelect') }}</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="e6 > 3" step="3" :editable="e6 > 2" class="mt-2">
        {{ $t('SendStep.editSelectContent') }}
      </v-stepper-step>
      <v-stepper-content step="3">
        <v-card outlined class="mb-12 pa-2">
          <edit-card
            v-if="mountedActivities"
            ref="editCard"
            :is-visible="e6 === 3"
            v-model="sendContent"
            :activities="activities"
            :is3mClient="Boolean(Number(settings.mmm_verify))"
            @alert3m="alertThreeM = true"
          />
        </v-card>
        <v-btn color="primary" @click="confirmStep(3)">{{ $t('SendStep.confirmContent') }}</v-btn>
      </v-stepper-content>

      <v-stepper-step step="4">
        {{ $t('SendStep.confirmSend') }}
      </v-stepper-step>
      <v-stepper-content step="4">
        <v-card outlined class="mb-12 pa-2">
          <v-container>
            <v-row dense>
              <v-col cols="12">
                {{ $t('SendStep.countSend', { count: countSendPatients }) }}
              </v-col>
              <v-col cols="12">
                {{ $t('SendStep.sendContent') }}
                <div v-if="Number(sendContent.type) === 1" class="ml-4">
                  {{ $t('SendStep.useActivity') }} - {{ showActivity().title }}
                  <template v-if="sendContent.activityContent">
                    <br/>{{ $t('SendStep.otherContent') }} {{ sendContent.activityContent }}
                  </template>
                </div>
                <div v-else class="ml-4">
                  <template v-if="sendContent.customImg">
                    {{ $t('SendStep.picture') }}
                    <v-img max-height="200" max-width="200" contain :src="showCustomImg()"/>
                  </template>
                  <template v-if="sendContent.customContent">
                    <br v-if="sendContent.customImg"/>{{ $t('SendStep.content') }} {{ sendContent.customContent }}
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
        <v-btn
          color="primary"
          :loading="sendEligibilityLoading || isPreparingSend"
          :disabled="sendActionDisabled"
          @click="confirmStep(4)"
        >
          {{ sendActionLabel }}
        </v-btn>
        <div v-if="sendEligibilityFailed" class="red-6--text mt-2">
          發送資格載入失敗，請重新整理後再試一次
        </div>
      </v-stepper-content>
      <v-dialog v-model="maxAlertDialog" max-width="500">
        <v-card>
          <v-card-title>
            {{ $t('SendStep.alertMax', { count: totalPatientCount }) }}
          </v-card-title>
          <v-card-actions class="flex-wrap justify-center justify-space-around">
            <v-btn color="primary" class="my-1" width="150" @click="confirmUseCondition">{{ $t('SendStep.accept') }}</v-btn>
            <v-btn color="red" class="white--text ml-0 my-1" width="150" @click="denyUseCondition">{{ $t('SendStep.refuse') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="activityExpiredDialog" max-width="560" persistent>
        <v-card>
          <v-card-title class="recipient-confirmation-text">活動時間已結束，請問您是否還要繼續發送群發訊息？</v-card-title>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              color="primary"
              :loading="isPreparingSend"
              :disabled="sendActionDisabled"
              @click="prepareSend"
            >{{ activityExpiredSendLabel }}</v-btn>
            <v-btn dark color="grey-5" :disabled="isSending" @click="cancelSendMessage">否</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="quotaDialog" max-width="560" :persistent="isSending">
        <v-card class="pa-2 pa-sm-4">
          <v-card-title class="recipient-confirmation-text">發送確認</v-card-title>
          <v-card-text v-if="recipientPreview" class="recipient-confirmation-text">
            <p class="text-body-1">{{ recipientConfirmationText }}</p>
            <template v-if="recipientPreview.can_send && recipientPreview.oa_type === 'own'">
              <div>
                目前剩餘LINE訊息約：<span class="text-decoration-underline">{{ quota }}</span> 則<br>
                本次預計使用：<span class="text-decoration-underline">{{ recipientPreview.count }}</span> 則
              </div>
              <p class="mt-4 mb-0">
                建議您前往LINE後台，確認訊息量是否足夠與加購。<br>
                若不足但選擇發出，將只有部分患者收到唷！
              </p>
            </template>
          </v-card-text>
          <v-card-actions class="flex-wrap">
            <v-btn v-if="recipientPreview && recipientPreview.can_send && recipientPreview.oa_type === 'own'"
                   color="grey-5" text block class="ma-0 mb-2" target="_blank" :href="$enum.DENTCO_CS_LINE">聯繫客服</v-btn>
            <v-spacer/>
            <v-btn v-if="recipientPreview && recipientPreview.can_send" text :disabled="isSending" @click="cancelSendMessage">取消</v-btn>
            <v-btn color="primary" :loading="isSending" :disabled="sendActionDisabled" @click="sendMessage">確認</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="alertThreeM" width="fit-content" max-width="380">
        <v-card>
          <v-card-title>此功能需要為3M氟漆客戶，如需開啟請洽客服</v-card-title>
          <v-card-actions>
            <v-btn
              class="mx-auto"
              color="primary"
              :href="$enum.DENTCO_CS_LINE"
              target="_blank"
              @click="alertThreeM = false"
            >聯繫客服
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-stepper>
  </div>
</template>

<script>
import {
  getChannelQuotaAPI,
  getClinicListAPI,
  getDoctorListAPI,
  getTreatmentTagsAPI,
  getMessageActivitiesAPI,
  getMessagePatientAPI,
  getMessageSettingAPI, getReserveTagAPI,
  previewMessageAPI,
  sendMessageAPI
} from "@/api";
import { mapGetters, mapState } from "vuex";
import { get_age, getGender } from "@/utils/filters";
import _ from "lodash";
import MidAutumnBanner from '@/components/message/MidAutumnBanner.vue';
import EditCard from "@/components/message/EditCard";
import { isExpired } from '@/utils/tool';
import GuideTour from "@/components/utils/GuideTour.vue";
import {
  buildLineBroadcastCooldownKey,
  getCooldownRemainingSeconds,
  LINE_BROADCAST_COOLDOWN_MS,
  readLineBroadcastCooldown,
  writeLineBroadcastCooldown
} from '@/utils/lineBroadcastCooldown';

export default {
  name: "SendStep",

  components: {GuideTour, EditCard, MidAutumnBanner},

  data() {
    return {
      loading: false,
      isSending: false,
      isPreparingSend: false,
      e6: 1,
      dates: [],
      menu: false,
      satisfaction: false,
      doctorItems: [],
      selectDoctors: [],
      clinicItems: [],
      selectClinic: [],
      treatmentTagItems: [],
      selectedTreatmentTagIds: [],
      patientName: null,
      selectReserveTags: [],
      reserveTags: [],
      patientItems: [],
      originalPatients: [],
      patients: [],
      searchPatientName: null,
      selectPatients: [],
      sendContent: {
        type: 1
      },
      activities: [],
      maxAlertDialog: false,
      useCondition: false,
      totalPatientCount: 0,
      mountedActivities: false,
      cooldownExpiresAt: 0,
      cooldownRemainingSeconds: 0,
      cooldownTimer: null,
      activityExpiredDialog: false,
      isExpired,
      alertThreeM: false,
      settings: {},
      messageSettingLoaded: false,
      sendEligibilityFailed: false,
      maxSelect: 500,
      quota: null,
      total: null,
      quotaDialog: false,
      recipientPreview: null,
      previewRequestId: 0,
      previewFingerprint: '',
    }
  },
  computed: {
    ...mapState({
      panoId: state => state.panoId,
      excludeMessageActivities: state => state.excludeMessageActivities,
    }),
    ...mapGetters([
      'axiosParamData',
      'clinicUser',
      'doctorUser',
      'medicalKind'
    ]),
    dateRangeText: {
      get: function () {
        return this.dates.join(' ~ ')
      },
      set: function () {
        this.dates = []
      }
    },
    icon() {
      let selectAll
      if (this.clinicUser)
        selectAll = this.selectDoctors.length === this.doctorItems.length
      else
        selectAll = this.selectClinic.length === this.clinicItems.length
      if (selectAll) return 'mdi-close-box'
      return 'mdi-checkbox-blank-outline'
    },
    selectAllPatients() {  // 是否已經將篩選出來的病患全部選擇了，只要有一個沒選擇，就不是
      return this.patients.every(v => this.selectPatients.includes(v))
    },
    countSendPatients() {
      return this.useCondition ? this.totalPatientCount : this.selectPatients.length
    },
    sendEligibilityLoaded() {
      return this.messageSettingLoaded
    },
    sendEligibilityLoading() {
      return !this.sendEligibilityLoaded && !this.sendEligibilityFailed
    },
    isCoolingDown() {
      return this.cooldownRemainingSeconds > 0
    },
    recipientTargetKey() {
      // Include the selection even in condition mode so switching the edited list invalidates confirmation.
      return JSON.stringify([this.buildRecipientPayload(), this.useCondition, this.selectPatients])
    },
    recipientConfirmationText() {
      if (!this.recipientPreview) return ''
      const count = this.recipientPreview.count
      return this.recipientPreview.can_send
        ? `去掉重複帳號為${count}人，請點擊確認發出`
        : `去掉重複帳號為${count}人，公用LINE帳號發送人數不可超過500人`
    },
    sendActionDisabled() {
      return this.isSending || this.isPreparingSend || this.isCoolingDown || !this.sendEligibilityLoaded
    },
    sendActionLabel() {
      if (this.isCoolingDown) return `請稍候 ${this.cooldownRemainingSeconds} 秒`
      return this.$t('SendStep.confirm')
    },
    activityExpiredSendLabel() {
      return this.isCoolingDown ? `請稍候 ${this.cooldownRemainingSeconds} 秒` : '是'
    }
  },
  watch: {
    recipientTargetKey() {
      this.invalidateRecipientPreview()
    },
    sendContent: {
      deep: true,
      handler() {
        this.invalidateRecipientPreview()
      }
    },
    quotaDialog(value) {
      if (!value) this.invalidateRecipientPreview()
    },
    searchPatientName(v) {
      if (v)
        this.patients = this.originalPatients.filter(a => a.includes(v));
      else
        this.patients = this.originalPatients
    }
  },
  methods: {
    toggle() {
      if (this.clinicUser) {
        const selectAll = this.selectDoctors.length === this.doctorItems.length
        this.$nextTick(() => {
          if (selectAll) {
            this.selectDoctors = []
          } else {
            this.selectDoctors = this.doctorItems.map(e => e.doctor_code)
          }
        })
      } else {
        const selectAll = this.selectClinic.length === this.clinicItems.length
        this.$nextTick(() => {
          if (selectAll) {
            this.selectClinic = []
          } else {
            this.selectClinic = this.clinicItems.map(e => e.clinic_code)
          }
        })
      }
    },
    filterPatient() {
      this.loading = true
      this.searchPatientName = null
      const treatmentTagIds = this.selectedTreatmentTagIds.join(',')
      const params = {
        ...this.axiosParamData,
        satisfaction: Number(this.satisfaction) || null,
        ...(treatmentTagIds ? { treatment_tag_ids: treatmentTagIds } : {}),
        filter_clinic: this.selectClinic.join(',') || null,
        filter_doctor: this.selectDoctors.join(',') || null,
        name: this.patientName || null,
        tag_ids: this.selectReserveTags
      }
      if (this.dates.length) {
        if (this.dates.length === 1) params.start_time = params.end_time = this.dates[0]
        else {
          params.start_time = this.dates[0]
          params.end_time = this.dates[1]
        }
      }

      getMessagePatientAPI(params).then(res => {
        this.totalPatientCount = Number(res.data.count)
        if (this.totalPatientCount === 0) {
          this.patients = []
          this.originalPatients = []
          this.selectPatients = []
          this.totalPatientCount = 0
          this.useCondition = false
          this.maxAlertDialog = false
          this.e6 = 1
          this.$notify.warning('目前沒有篩出此標籤的患者，請重新篩選')
          return
        }
        if (this.totalPatientCount > this.maxSelect) {
          this.maxAlertDialog = true
          return
        }
        const r = res.data.data
        this.patients = this.originalPatients = r.map(e => {
          let _ = `${e.patient_code}-${e.name}-${e.phone} ${e.name}`
          if (e.birthday) _ += ` ${get_age(parseInt(e.birthday) * 1000)}${this.$t('SendStep.age')}`
          if (getGender(e.sex)) _ += ` ${getGender(e.sex)}${this.$t('SendStep.genderUnit')}`
          return _
        })
        this.useCondition = false
        this.e6 = 2
      }).finally(() => {
        this.loading = false
      })
    },
    clearFilter() {
      this.satisfaction = this.$options.data().satisfaction
      this.selectedTreatmentTagIds = this.$options.data().selectedTreatmentTagIds
      this.selectDoctors = this.$options.data().selectDoctors
      this.selectClinic = this.$options.data().selectClinic
      this.patientName = this.$options.data().patientName
      this.selectReserveTags = this.$options.data().selectReserveTags
      this.dates = this.$options.data().dates
    },
    confirmUseCondition() {
      this.useCondition = true
      this.e6 = 3
      this.maxAlertDialog = false
    },
    denyUseCondition() {
      this.useCondition = false
      this.maxAlertDialog = false
    },
    remove(index) {
      this.selectPatients.splice(index, 1)
    },
    toggleSelectPatient() {
      this.$nextTick(() => {
        if (this.selectAllPatients) {
          this.selectPatients = _.difference(this.selectPatients, this.patients)
        } else {
          const tmp = _.union(this.selectPatients, this.patients)
          if (tmp.length > this.maxSelect) {
            return this.$notify.warning(`最多可選擇 ${this.maxSelect} 位患者`)
          }
          this.selectPatients = _.union(this.selectPatients, this.patients)
        }
      })
    },
    selectPatient(patient) {
      const index = this.selectPatients.indexOf(patient)
      if (index === -1) {
        this.selectPatients.length >= this.maxSelect ? this.$notify.warning(`最多可選擇 ${this.maxSelect} 位患者`) : this.selectPatients.push(patient)
      } else {
        this.selectPatients.splice(index, 1)
      }
    },
    showActivity() {
      return this.activities.find(e => String(e.id) === String(this.sendContent.activity)) || {}
    },
    showCustomImg() {
      if (this.sendContent.customImg)
        return URL.createObjectURL(this.sendContent.customImg)
    },
    getCooldownStorageKey() {
      return buildLineBroadcastCooldownKey(this.axiosParamData)
    },
    stopCooldownTimer() {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    },
    syncCooldownState() {
      this.cooldownRemainingSeconds = getCooldownRemainingSeconds(this.cooldownExpiresAt)
      if (this.cooldownRemainingSeconds > 0) return

      const storedExpiresAt = readLineBroadcastCooldown(this.getCooldownStorageKey())
      if (storedExpiresAt > this.cooldownExpiresAt) {
        this.cooldownExpiresAt = storedExpiresAt
        this.cooldownRemainingSeconds = getCooldownRemainingSeconds(storedExpiresAt)
        return
      }

      this.cooldownExpiresAt = 0
      this.stopCooldownTimer()
    },
    startCooldownTimer() {
      this.stopCooldownTimer()
      this.syncCooldownState()
      if (!this.cooldownRemainingSeconds) return

      this.cooldownTimer = setInterval(() => {
        this.syncCooldownState()
      }, 1000)
    },
    restoreCooldown() {
      this.cooldownExpiresAt = readLineBroadcastCooldown(this.getCooldownStorageKey())
      this.startCooldownTimer()
    },
    refreshCooldownFromStorage() {
      const storedExpiresAt = readLineBroadcastCooldown(this.getCooldownStorageKey())
      if (storedExpiresAt > this.cooldownExpiresAt) {
        this.cooldownExpiresAt = storedExpiresAt
        this.startCooldownTimer()
        return
      }

      this.syncCooldownState()
    },
    handleCooldownStorage(event) {
      if (event.key !== this.getCooldownStorageKey()) return
      this.refreshCooldownFromStorage()
    },
    activateCooldown() {
      this.cooldownExpiresAt = Date.now() + LINE_BROADCAST_COOLDOWN_MS
      writeLineBroadcastCooldown(this.getCooldownStorageKey(), this.cooldownExpiresAt)
      this.startCooldownTimer()
    },
    ensureCanStartSend() {
      if (this.isSending || this.isPreparingSend) return false

      this.refreshCooldownFromStorage()

      if (!this.sendEligibilityLoaded) {
        const message = this.sendEligibilityFailed
          ? '發送資格載入失敗，請重新整理後再試一次'
          : '發送資格載入中，請稍候再試一次'
        this.$notify.alert(message)
        return false
      }

      if (this.isCoolingDown) {
        this.$notify.alert(`群發訊息需間隔一分鐘發送，請稍候 ${this.cooldownRemainingSeconds} 秒後再試一次`)
        return false
      }

      if (this.useCondition && this.totalPatientCount <= 0) {
        this.$notify.warning(this.$t('SendStep.pleaseChoosePatient'))
        this.e6 = 1
        return false
      }

      if (!this.useCondition && !this.selectPatients.length) {
        this.$notify.warning(this.$t('SendStep.pleaseChoosePatient'))
        this.e6 = 2
        return false
      }

      return true
    },
    validateMessageContent() {
      if (Number(this.sendContent.type) === 1) {
        if (Number(this.sendContent.activity) === Number(this.panoId) && !this.sendContent.panoImgId) {
          this.$notify.warning('請先上傳 pano報告再繼續')
          return false
        }
        if (this.sendContent.activity) return true
        this.$notify.warning(this.$t('SendStep.pleaseChooseActivity'))
      } else if (Number(this.sendContent.type) === 2) {
        if (this.sendContent.customImg || this.sendContent.customContent) return true
        this.$notify.warning(this.$t('SendStep.pleaseEditContent'))
      }
      return false
    },
    async confirmStep(n) {
      if (n === 3) {
        if (this.validateMessageContent()) this.e6 = 4
        return
      }
      if (n !== 4 || !this.ensureCanStartSend() || !this.validateMessageContent()) return
      if (Number(this.sendContent.type) === 1 && this.isExpired(this.showActivity().end_date)) {
        this.activityExpiredDialog = true
        return
      }
      await this.prepareSend()
    },
    buildRecipientPayload() {
      const treatmentTagIds = this.selectedTreatmentTagIds.join(',')
      const data = {
        ...this.axiosParamData,
        message_type: Number(this.sendContent.activity) === Number(this.panoId) ? 3 : Number(this.sendContent.type),
        activity: this.sendContent.activity || 0,
        pano_id: this.sendContent.panoImgId || 0,
        satisfaction: Number(this.satisfaction) || null,
        ...(treatmentTagIds ? { treatment_tag_ids: treatmentTagIds } : {}),
        filter_clinic: this.selectClinic.join(',') || null,
        filter_doctor: this.selectDoctors.join(',') || null,
        name: this.patientName || null,
        tag_ids: [...this.selectReserveTags],
      }
      if (this.dates.length) {
        data.start_time = this.dates[0]
        data.end_time = this.dates[1] || this.dates[0]
      }
      if (!this.useCondition) data.patient_code = this.selectPatients.map(e => e.split('-')[0]).join(',')
      return data
    },
    invalidateRecipientPreview() {
      this.previewRequestId++
      this.recipientPreview = null
      this.previewFingerprint = ''
      this.quotaDialog = false
    },
    async prepareSend() {
      // Flush edits before capturing the request; reactive watchers also invalidate A-B-A changes.
      await this.$nextTick()
      if (!this.ensureCanStartSend() || !this.validateMessageContent()) return
      this.invalidateRecipientPreview()
      this.activityExpiredDialog = false
      this.isPreparingSend = true
      const requestId = this.previewRequestId
      const fingerprint = this.recipientTargetKey
      const isCurrent = () => requestId === this.previewRequestId && fingerprint === this.recipientTargetKey
      try {
        const response = await previewMessageAPI(this.buildRecipientPayload())
        if (!isCurrent()) return
        const result = response.data
        const count = Number(result?.count)
        const countValid = /^(0|[1-9]\d*)$/.test(String(result?.count)) && Number.isSafeInteger(count)
        if (!countValid || typeof result.can_send !== 'boolean' || !['own', 'public'].includes(result.oa_type) ||
            ![null, 500].includes(result.limit) || ![null, 'public_oa_limit', 'no_recipients'].includes(result.reason) ||
            (result.can_send && (count === 0 || result.reason !== null)) ||
            (!result.can_send && count > 0 && result.reason !== 'public_oa_limit')) {
          throw new Error('Invalid recipient preview')
        }
        if (!count) {
          this.$notify.warning('目前沒有可發送的LINE帳號，請重新選擇發送對象')
          return
        }
        if (result.can_send && result.oa_type === 'own' && !await this.getChannelQuota()) return
        if (!isCurrent()) return
        this.recipientPreview = { ...result, count }
        this.previewFingerprint = fingerprint
        this.quotaDialog = true
      } catch (error) {
        if (isCurrent()) this.$notify.alert('無法取得發送人數，請稍後再試一次')
      } finally {
        this.isPreparingSend = false
      }
    },
    buildMessagePayload(data) {
      if (!this.sendContent.customImg) return data

      const formData = new FormData()
      for (const [key, value] of Object.entries(data)) {
        if (value === null || typeof value === 'undefined') continue
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(`${key}[]`, item))
          continue
        }
        formData.append(key, value)
      }
      return formData
    },
    async sendMessage() {
      if (!this.quotaDialog || !this.recipientPreview || this.isSending) return
      if (!this.recipientPreview.can_send) {
        this.cancelSendMessage()
        return
      }
      if (this.previewFingerprint !== this.recipientTargetKey) {
        this.invalidateRecipientPreview()
        return
      }
      if (!this.ensureCanStartSend() || !this.validateMessageContent()) return

      const data = {
        ...this.buildRecipientPayload(),
        expected_recipient_count: this.recipientPreview.count,
        image: this.sendContent.customImg,
        content: Number(this.sendContent.type) === 2 ? this.sendContent.customContent : this.sendContent.activityContent,
      }
      this.isSending = true
      this.loading = true
      this.invalidateRecipientPreview()
      let needsPreview = false
      try {
        const response = await sendMessageAPI(this.buildMessagePayload(data))
        if (String(response.data?.status) === '0') {
          this.handleSendRejection({ response: { status: 422, data: response.data } })
          return
        }
        this.completeSendMessage()
      } catch (error) {
        needsPreview = error.response?.status === 409
        if (!this.handleSendRejection(error)) this.$notify.alert('發送失敗，請稍後再試一次')
      } finally {
        this.loading = false
        this.isSending = false
      }
      if (needsPreview) await this.prepareSend()
    },
    handleSendRejection(error) {
      const response = error.response
      const data = response?.data
      const isRejectedSend = [409, 422].includes(response?.status)
      if (!isRejectedSend) return false

      this.invalidateRecipientPreview()
      this.activityExpiredDialog = false

      const rawCount = data?.count
      const count = Number(rawCount)
      const countText = rawCount !== '' && rawCount !== null && typeof rawCount !== 'undefined' &&
        Number.isFinite(count) && count >= 0
        ? `後端重新計算發送對象：${count} 人`
        : ''
      const message = typeof data?.messages === 'string' && data.messages.trim()
        ? data.messages.trim()
        : '發送條件不符，請調整發送對象後再試一次'

      this.$notify.alert([countText, message].filter(Boolean).join('\n'))
      return true
    },
    completeSendMessage() {
      this.quotaDialog = false
      this.activityExpiredDialog = false
      this.activateCooldown()
      this.resetAll()
      this.e6 = 1
      this.openTodayLineGroupLogs()
    },
    openTodayLineGroupLogs() {
      const today = this.$moment().format('YYYY-MM-DD')
      this.$router.push({
        name: 'messageLog',
        query: {
          mode: 'lineGroup',
          start: today,
          end: today,
        }
      }, undefined, () => {
        this.$notify.alert('訊息已送出，但無法自動開啟發送紀錄，請由選單進入發送紀錄查看')
      })
    },
    cancelSendMessage() {
      if (this.isSending) return
      this.invalidateRecipientPreview()
      this.loading = false
      this.activityExpiredDialog = false
    },
    resetAll() {
      this.sendContent = {
        type: 1
      }
      this.$refs.editCard?.resetPano()
      this.selectPatients = []
      this.satisfaction = false
      this.dates = []
      this.selectClinic = []
      this.selectDoctors = []
      this.selectedTreatmentTagIds = []
      this.selectReserveTags = []
      this.patientName = null
      this.searchPatientName = null
      this.totalPatientCount = 0
      this.useCondition = false
    },
    async getMsgSetting() {
      try {
        const res = await getMessageSettingAPI(this.axiosParamData)
        const ownChannel = res.data?.own_channel
        if (![true, false, 0, 1, '0', '1'].includes(ownChannel)) throw new Error('Invalid own_channel')

        this.settings = res.data
        this.messageSettingLoaded = true
      } catch (error) {
        this.sendEligibilityFailed = true
      }
    },
    async getChannelQuota() {
      try {
        const res = await getChannelQuotaAPI(this.axiosParamData)
        this.quota = Number(res.data.quota) - Number(res.data.total_usage)
        return true
      } catch (error) {
        this.$notify.alert('無法取得訊息餘額，請稍後再試一次')
        return false
      }
    }
  },
  mounted() {
    this.restoreCooldown()
    window.addEventListener('storage', this.handleCooldownStorage)
    this.getMsgSetting()
    if (this.clinicUser)
      getDoctorListAPI(this.axiosParamData).then(res => {
        this.doctorItems = res.data
      })
    else
      getClinicListAPI(this.axiosParamData).then(res => {
        this.clinicItems = res.data
      })
    getTreatmentTagsAPI(this.axiosParamData).then(res => {
      this.treatmentTagItems = res.data
    })
    getMessageActivitiesAPI({...this.axiosParamData, kind: this.medicalKind}).then(res => {
      let excludeMessageActivities
      this.mountedActivities = true

      if (this.doctorUser)
        excludeMessageActivities = this.excludeMessageActivities.doctor

      if (this.clinicUser)
        excludeMessageActivities = this.excludeMessageActivities.clinic

      this.activities = res.data.data.filter(function(e) {
        return !excludeMessageActivities.includes(e.id)
      })
    })
    getReserveTagAPI(this.axiosParamData).then(res => {
      this.reserveTags = res.data.filter(tag => !!tag.content)
    })
  },
  beforeDestroy() {
    this.invalidateRecipientPreview()
    this.stopCooldownTimer()
    window.removeEventListener('storage', this.handleCooldownStorage)
  }
}
</script>

<style scoped>
.recipient-confirmation-text {
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
  min-width: 0;
}
</style>
